/** Поведение всех четырёх типов задаётся здесь: ветвлений по типу в компонентах быть не должно */

import { isTaskTypeSelectable, taskTypeDisabledReasonKey } from '../../config/features';
import { MM_TASK_TYPES, MmTaskType } from '../../types/enums';
import { MmTaskFilters } from '../../types/dto';
import {
  MmTaskCreateFormatCatalog,
  resolveCreateEffectiveFormats,
} from './buildTaskCreatePayload';

const KEY = 'priceMonitoring.mobileMonitoring.';

export interface MmTaskTypeProfile {
  type: MmTaskType;

  labelKey: string;

  typeExplanationKey: string;

  productFiltersTitleKey: string;

  productFiltersHintKey: string;

  hasProductsTable: boolean;

  hasSelectCheckBoxesData: boolean;

  showMonitoringLevelHint: boolean;

  hasPriceChecker: boolean;

  /** Пустая строка — у типов без таблицы товаров, а не «забыли заполнить» */
  productsEmptyKey: string;

  minCategories: number;

  subCategoriesConditional: boolean;
}

export const MM_TASK_TYPE_PROFILES: { [type: string]: MmTaskTypeProfile } = {
  [MmTaskType.KVI]: {
    type: MmTaskType.KVI,
    labelKey: KEY + 'taskTypes.kvi',
    typeExplanationKey: KEY + 'taskForm.typeExplanationKvi',
    productFiltersTitleKey: KEY + 'taskForm.productSearchTitle',
    productFiltersHintKey: KEY + 'taskForm.productSearchHint',
    hasProductsTable: true,
    hasSelectCheckBoxesData: true,
    showMonitoringLevelHint: false,
    hasPriceChecker: true,
    productsEmptyKey: KEY + 'taskForm.productsEmptyKvi',
    minCategories: 0,
    subCategoriesConditional: false,
  },
  [MmTaskType.FREE]: {
    type: MmTaskType.FREE,
    labelKey: KEY + 'taskTypes.free',
    typeExplanationKey: KEY + 'taskForm.typeExplanationFree',
    productFiltersTitleKey: KEY + 'taskForm.productSearchTitle',
    productFiltersHintKey: KEY + 'taskForm.productSearchHint',
    hasProductsTable: true,
    hasSelectCheckBoxesData: true,
    showMonitoringLevelHint: false,
    hasPriceChecker: true,
    productsEmptyKey: KEY + 'taskForm.productsEmpty',
    minCategories: 0,
    subCategoriesConditional: false,
  },
  [MmTaskType.TPC]: {
    type: MmTaskType.TPC,
    labelKey: KEY + 'taskTypes.tpc',
    typeExplanationKey: KEY + 'taskForm.typeExplanationTpc',
    productFiltersTitleKey: KEY + 'taskForm.monitoringCriteriaTitle',
    productFiltersHintKey: KEY + 'taskForm.monitoringCriteriaHintTpc',
    hasProductsTable: false,
    hasSelectCheckBoxesData: false,
    showMonitoringLevelHint: false,
    hasPriceChecker: false,
    productsEmptyKey: '',
    minCategories: 1,
    subCategoriesConditional: true,
  },
  [MmTaskType.CONTINUOUS]: {
    type: MmTaskType.CONTINUOUS,
    labelKey: KEY + 'taskTypes.continuous',
    typeExplanationKey: KEY + 'taskForm.typeExplanationContinuous',
    productFiltersTitleKey: KEY + 'taskForm.monitoringCriteriaTitle',
    productFiltersHintKey: KEY + 'taskForm.monitoringCriteriaHintContinuous',
    hasProductsTable: false,
    hasSelectCheckBoxesData: false,
    showMonitoringLevelHint: true,
    hasPriceChecker: false,
    productsEmptyKey: '',
    minCategories: 0,
    subCategoriesConditional: false,
  },
};

export function getTaskTypeProfile(type: MmTaskType | null): MmTaskTypeProfile | null {
  if (type === null) {
    return null;
  }

  const profile = MM_TASK_TYPE_PROFILES[type];

  return profile ? profile : null;
}

export interface MmTaskTypeListOption {
  type: MmTaskType;
  labelKey: string;
  isDisabled: boolean;
  disabledReasonKey: string | null;
}

export function listTaskTypeOptions(): MmTaskTypeListOption[] {
  return MM_TASK_TYPES.map(function (type: MmTaskType): MmTaskTypeListOption {
    const profile = getTaskTypeProfile(type);

    return {
      type: type,
      labelKey: profile !== null ? profile.labelKey : '',
      isDisabled: !isTaskTypeSelectable(type),
      disabledReasonKey: taskTypeDisabledReasonKey(type),
    };
  });
}

/** Фильтры приходят уже подготовленными: иначе «Все» (0) сойдёт за выбранный формат */
export interface MmTaskCreateValidationContext {
  hasTradingNetwork: boolean;
  type: MmTaskType | null;
  filters: MmTaskFilters;
  /** null — справочник форматов ещё грузится */
  loadedFormatIds?: MmTaskCreateFormatCatalog;
  clientHasSubcategories: boolean;
  isSubcategoriesLoading: boolean;
  selectedProductsCount: number;
  isSubmitting: boolean;
}

/** Возвращается первое нарушение: порядок веток повторяет порядок блоков формы сверху вниз */
export function resolveCreateBlockReasonKey(ctx: MmTaskCreateValidationContext): string | null {
  if (!ctx.hasTradingNetwork) {
    return KEY + 'taskForm.blockNoNetwork';
  }

  const profile = getTaskTypeProfile(ctx.type);
  const catalog = ctx.loadedFormatIds !== undefined ? ctx.loadedFormatIds : null;
  const formats = ctx.loadedFormatIds !== undefined
    ? resolveCreateEffectiveFormats(ctx.filters.formats, catalog)
    : ctx.filters.formats;

  if (profile === null) {
    return KEY + 'taskForm.blockNoType';
  }

  if (formats.length === 0) {
    return KEY + 'taskForm.blockNoFormat';
  }

  if (ctx.filters.groups.length === 0) {
    return KEY + 'taskForm.blockNoGroup';
  }

  if (ctx.filters.categories.length < profile.minCategories) {
    return KEY + 'taskForm.blockNoCategoryTpc';
  }

  if (profile.subCategoriesConditional) {
    if (ctx.isSubcategoriesLoading) {
      return KEY + 'taskForm.blockSubcategoriesLoading';
    }

    if (ctx.clientHasSubcategories && ctx.filters.subCategories.length === 0) {
      return KEY + 'taskForm.blockNoSubcategoryTpc';
    }
  }

  /** Пустую выборку товаров блокирует только фронт: серверной проверки на это нет */
  if (profile.hasProductsTable && ctx.selectedProductsCount === 0) {
    return KEY + 'taskForm.blockNoProducts';
  }

  if (ctx.isSubmitting) {
    return KEY + 'taskForm.blockSubmitting';
  }

  return null;
}

export function canLoadProducts(
  filters: MmTaskFilters,
  loadedFormatIds?: MmTaskCreateFormatCatalog,
): boolean {
  const formats = loadedFormatIds !== undefined
    ? resolveCreateEffectiveFormats(filters.formats, loadedFormatIds)
    : filters.formats;

  return formats.length > 0 && filters.groups.length > 0;
}

export function resolveProductsThresholdKey(
  filters: MmTaskFilters,
  loadedFormatIds?: MmTaskCreateFormatCatalog,
): string | null {
  const formats = loadedFormatIds !== undefined
    ? resolveCreateEffectiveFormats(filters.formats, loadedFormatIds)
    : filters.formats;
  const noFormat = formats.length === 0;
  const noGroup = filters.groups.length === 0;

  if (noFormat && noGroup) {
    return KEY + 'taskForm.needFormatAndGroup';
  }

  if (noFormat) {
    return KEY + 'taskForm.needFormat';
  }

  if (noGroup) {
    return KEY + 'taskForm.needGroup';
  }

  return null;
}

export interface MmResultColumnSpec {
  /** Ключ совпадает с ключом колонки таблицы и именем в настройке колонок */
  key: string;
  titleKey: string;
  /** Все колонки рисуются слотами: customRender в ant-design-vue 1.7.8 ячейку не форматирует */
  slot: string;
  /** null — своей ширины нет: колонка резиновая и получает MM_RESULT_COLUMN_AUTO_WIDTH как минимум */
  width: number | null;
  sortable: boolean;
  defaultVisible: boolean;
  toggleable: boolean;
}

/** Одна незакреплённая колонка остаётся без явной ширины, иначе ant рвёт закреплённую накладку */
export const MM_RESULT_COLUMN_AUTO_WIDTH = 220;

export const MM_RESULT_COLUMN_CATALOG: { [key: string]: MmResultColumnSpec } = {
  classification: {
    key: 'classification', titleKey: KEY + 'taskResults.colLevel', slot: 'classificationCell',
    width: 200, sortable: false, defaultVisible: true, toggleable: true,
  },

  photo: {
    key: 'photo', titleKey: KEY + 'taskResults.colPhoto', slot: 'photoCell',
    width: 88, sortable: false, defaultVisible: true, toggleable: true,
  },
  productName: {
    key: 'productName', titleKey: KEY + 'taskResults.colProductName', slot: 'productNameCell',
    width: null, sortable: true, defaultVisible: true, toggleable: true,
  },
  barCode: {
    key: 'barCode', titleKey: KEY + 'taskResults.colBarCode', slot: 'barCodeCell',
    width: 150, sortable: false, defaultVisible: false, toggleable: true,
  },
  competitorSku: {
    key: 'competitorSku', titleKey: KEY + 'taskResults.colCompetitorSku',
    slot: 'competitorSkuCell',
    width: 150, sortable: false, defaultVisible: false, toggleable: true,
  },
  volume: {
    key: 'volume', titleKey: KEY + 'taskResults.colVolume', slot: 'volumeCell',
    width: 120, sortable: false, defaultVisible: false, toggleable: true,
  },
  price: {
    key: 'price', titleKey: KEY + 'taskResults.colPrice', slot: 'priceCell',
    width: 140, sortable: true, defaultVisible: true, toggleable: true,
  },
  promo: {
    key: 'promo', titleKey: KEY + 'taskResults.colPromo', slot: 'promoCell',
    width: 150, sortable: false, defaultVisible: true, toggleable: true,
  },
  outcome: {
    key: 'outcome', titleKey: KEY + 'taskResults.colOutcome', slot: 'outcomeCell',
    width: 210, sortable: false, defaultVisible: true, toggleable: false,
  },
  capturedAt: {
    key: 'capturedAt', titleKey: KEY + 'taskResults.colCapturedAt', slot: 'capturedAtCell',
    width: 160, sortable: true, defaultVisible: true, toggleable: true,
  },
  receivedAt: {
    key: 'receivedAt', titleKey: KEY + 'taskResults.colReceivedAt', slot: 'receivedAtCell',
    width: 160, sortable: false, defaultVisible: false, toggleable: true,
  },
  auditor: {
    key: 'auditor', titleKey: KEY + 'taskResults.colAuditor', slot: 'auditorCell',
    width: 150, sortable: false, defaultVisible: false, toggleable: true,
  },
  comment: {
    key: 'comment', titleKey: KEY + 'taskResults.colComment', slot: 'commentCell',
    width: 180, sortable: false, defaultVisible: false, toggleable: true,
  },
  /** Ширина 240: при меньшей иконка прежней цены вылезает в соседнюю колонку */
  review: {
    key: 'review', titleKey: KEY + 'taskResults.colReview', slot: 'reviewCell',
    width: 240, sortable: false, defaultVisible: true, toggleable: true,
  },
  priceSource: {
    key: 'priceSource', titleKey: KEY + 'taskResults.colPriceSource', slot: 'priceSourceCell',
    width: 150, sortable: false, defaultVisible: false, toggleable: true,
  },
  confidence: {
    key: 'confidence', titleKey: KEY + 'taskResults.colConfidence', slot: 'confidenceCell',
    width: 130, sortable: false, defaultVisible: false, toggleable: true,
  },
  /** Ширина 210 — под 5 круглых кнопок 30px с зазором 5px и отступами ячейки */
  actions: {
    key: 'actions', titleKey: KEY + 'taskResults.colActions', slot: 'actionsCell',
    width: 210, sortable: false, defaultVisible: true, toggleable: false,
  },

  tpcSubcategory: {
    key: 'tpcSubcategory', titleKey: KEY + 'taskResults.colSubcategory', slot: 'tpcSubcategoryCell',
    width: 240, sortable: false, defaultVisible: true, toggleable: false,
  },

  ourProduct: {
    key: 'ourProduct', titleKey: KEY + 'taskResults.colOurProduct', slot: 'ourProductCell',
    width: 240, sortable: false, defaultVisible: true, toggleable: false,
  },
  foundKind: {
    key: 'foundKind', titleKey: KEY + 'taskResults.colFoundKind', slot: 'foundKindCell',
    width: 150, sortable: false, defaultVisible: true, toggleable: true,
  },

  expectedPrice: {
    key: 'expectedPrice', titleKey: KEY + 'taskResults.colExpectedPrice', slot: 'expectedPriceCell',
    width: 150, sortable: false, defaultVisible: true, toggleable: true,
  },
  deviation: {
    key: 'deviation', titleKey: KEY + 'taskResults.colDeviation', slot: 'deviationCell',
    width: 110, sortable: false, defaultVisible: true, toggleable: true,
  },
  priceChecker: {
    key: 'priceChecker', titleKey: KEY + 'taskResults.colPriceChecker', slot: 'priceCheckerCell',
    width: 130, sortable: false, defaultVisible: true, toggleable: true,
  },

  /** sortable false: подпись задания доклеивается на клиенте, сервер по ней не сортирует */
  taskRef: {
    key: 'taskRef', titleKey: KEY + 'taskResults.colTask', slot: 'taskRefCell',
    width: 190, sortable: false, defaultVisible: true, toggleable: false,
  },
  taskTypeBadge: {
    key: 'taskTypeBadge', titleKey: KEY + 'taskResults.colTaskType', slot: 'taskTypeCell',
    width: 150, sortable: false, defaultVisible: true, toggleable: true,
  },
  competitorStore: {
    key: 'competitorStore', titleKey: KEY + 'taskResults.colCompetitorStore',
    slot: 'competitorStoreCell',
    width: 200, sortable: false, defaultVisible: true, toggleable: true,
  },
};

export const MM_RESULT_BATCH_COLUMN_KEYS: string[] =
  ['taskRef', 'taskTypeBadge', 'competitorStore'];

export type MmResultColumnScope = 'task' | 'batch';

/** В сводном режиме агрегатов классификаций нет, и колонка уровня была бы пустой у всех строк */
function dropLevelColumn(columnKeys: string[]): string[] {
  const result: string[] = [];

  for (let i = 0; i < columnKeys.length; i += 1) {
    if (columnKeys[i] !== 'classification') {
      result.push(columnKeys[i]);
    }
  }

  return result;
}

function withBatchColumns(columnKeys: string[]): string[] {
  const anchor = columnKeys.indexOf('productName');
  const head = anchor >= 0 ? columnKeys.slice(0, anchor + 1) : [];
  const tail = anchor >= 0 ? columnKeys.slice(anchor + 1) : columnKeys.slice(0);

  return head.concat(MM_RESULT_BATCH_COLUMN_KEYS, tail);
}

export interface MmTaskTypeResultProfile {
  columnKeys: string[];
  /** У ТПЦ ключ строки — карточка уровня: от этого зависят подпись строки и знаменатель приёмки */
  levelIsRowKey: boolean;
  showsMissingRows: boolean;
  canDeletePositions: boolean;
  detectsDuplicates: boolean;
}

const RESULT_BASE_TAIL = ['outcome', 'capturedAt', 'receivedAt', 'auditor', 'comment', 'review',
  'priceSource', 'confidence', 'actions'];

/** База колонок общая для всех четырёх типов; у Сплошного нет плановой цены и отклонения */
export const MM_TASK_TYPE_RESULT_PROFILES: { [type: string]: MmTaskTypeResultProfile } = {
  [MmTaskType.CONTINUOUS]: {
    columnKeys: ['classification', 'photo', 'productName', 'barCode', 'competitorSku', 'volume',
      'price', 'promo']
      .concat(RESULT_BASE_TAIL),
    levelIsRowKey: false,
    showsMissingRows: false,
    canDeletePositions: true,
    detectsDuplicates: true,
  },
  [MmTaskType.TPC]: {
    columnKeys: ['tpcSubcategory', 'photo', 'productName', 'barCode', 'competitorSku', 'volume',
      'price', 'promo']
      .concat(RESULT_BASE_TAIL),
    levelIsRowKey: true,
    showsMissingRows: true,
    canDeletePositions: false,
    detectsDuplicates: false,
  },
  [MmTaskType.FREE]: {
    columnKeys: ['ourProduct', 'photo', 'productName', 'foundKind', 'barCode', 'competitorSku',
      'volume', 'price', 'promo', 'priceChecker']
      .concat(RESULT_BASE_TAIL),
    levelIsRowKey: false,
    showsMissingRows: true,
    canDeletePositions: false,
    detectsDuplicates: false,
  },
  [MmTaskType.KVI]: {
    columnKeys: ['ourProduct', 'photo', 'productName', 'barCode', 'competitorSku', 'volume',
      'expectedPrice', 'price',
      'deviation', 'promo', 'priceChecker'].concat(RESULT_BASE_TAIL),
    levelIsRowKey: false,
    showsMissingRows: true,
    canDeletePositions: false,
    detectsDuplicates: false,
  },
};

export const MM_RESULT_FALLBACK_COLUMN_KEYS: string[] =
  ['photo', 'productName', 'barCode', 'competitorSku', 'volume', 'price', 'promo']
    .concat(RESULT_BASE_TAIL);

export function getTaskTypeResultProfile(type: MmTaskType | null): MmTaskTypeResultProfile | null {
  if (type === null) {
    return null;
  }

  const profile = MM_TASK_TYPE_RESULT_PROFILES[type];

  return profile ? profile : null;
}

export function listResultColumns(
  type: MmTaskType | null,
  scope?: MmResultColumnScope,
): MmResultColumnSpec[] {
  const profile = getTaskTypeResultProfile(type);
  const baseKeys = profile !== null ? profile.columnKeys : MM_RESULT_FALLBACK_COLUMN_KEYS;
  const columnKeys = scope === 'batch' ? withBatchColumns(dropLevelColumn(baseKeys)) : baseKeys;

  const specs: MmResultColumnSpec[] = [];

  for (let i = 0; i < columnKeys.length; i += 1) {
    const spec = MM_RESULT_COLUMN_CATALOG[columnKeys[i]];

    if (spec) {
      specs.push(spec);
    }
  }

  return specs;
}

export function listDefaultVisibleResultColumns(
  type: MmTaskType | null,
  scope?: MmResultColumnScope,
): string[] {
  const specs = listResultColumns(type, scope);
  const keys: string[] = [];

  for (let i = 0; i < specs.length; i += 1) {
    if (specs[i].defaultVisible) {
      keys.push(specs[i].key);
    }
  }

  return keys;
}
